/**
 * The TSR finding card, for the editor and the preview.
 *
 * Deliberately a mirror of the FINDING_CARD_* constants and the
 * findingCard* resolvers in backend/utils/report-helpers.js, as
 * owaspFindingsDefaults.js and walkthroughColumns.js already mirror theirs.
 * The field lookups and the row pairing are mirrored too, not just the
 * labels: what the preview draws has to be what the renderers print, and the
 * pairing in particular is a rule nobody would guess from looking at a config.
 */
import { OWASP_TOP10 } from '../../constants/owasp';

export const FINDING_CARD_FIELDS = {
  uid:               { label: 'Finding ID' },
  title:             { label: 'Title' },
  severity:          { label: 'Severity' },
  cvssScore:         { label: 'CVSS score with rating' },
  cvssScoreOnly:     { label: 'CVSS score' },
  cvssVector:        { label: 'CVSS vector' },
  status:            { label: 'Status' },
  cwe:               { label: 'CWE' },
  owasp:             { label: 'OWASP category' },
  exploitability:    { label: 'Exploitability' },
  remediationEffort: { label: 'Remediation effort' },
  affectedAsset:     { label: 'Affected asset' },
  details:           { label: 'Details' },
  observation:       { label: 'Observation' },
  stepsToReproduce:  { label: 'Steps to reproduce' },
  recommendation:    { label: 'Recommendation' },
  references:        { label: 'References' },
};

export const FINDING_CARD_FIELD_KEYS = Object.keys(FINDING_CARD_FIELDS);

export const FINDING_CARD_META_ROWS = [
  { label: 'CVSS v3.1 Score', field: 'cvssScore' },
  { label: 'Status', field: 'status' },
  { label: 'CVSS Vector', field: 'cvssVector', wide: true },
  { label: 'CWE', field: 'cwe' },
  { label: 'OWASP Category', field: 'owasp' },
  { label: 'Exploitability', field: 'exploitability' },
  { label: 'Remediation Effort', field: 'remediationEffort' },
  { label: 'Affected Asset', field: 'affectedAsset', wide: true },
];

export const FINDING_CARD_BODY_SECTIONS = [
  { label: 'Description', field: 'details' },
  { label: 'Business Impact', field: 'observation' },
  { label: 'Evidence', field: 'stepsToReproduce', evidence: true },
  { label: 'Recommendation', field: 'recommendation' },
  { label: 'References', field: 'references' },
];

export const FINDING_CARD_SEVERITY_BG = {
  Critical: '#7f1d1d', High: '#dc2626', Medium: '#ea580c',
  Low: '#ca8a04', Info: '#2e86c1', Informational: '#2e86c1',
};
export const FINDING_CARD_FALLBACK_BG = '#6b7280';
export const FINDING_CARD_EMPTY = 'No findings matched this section.';
export const FINDING_CARD_FIGURE_PREFIX = 'Figure';
export const FINDING_CARD_GRID_COLOR = '#d8dfe8';

/**
 * Whether a card draws its grid, and in what colour.
 *
 * The colour is resolved here rather than at each call site because Word used
 * to want both a ticked box AND a parseable colour before drawing anything,
 * while the PDF and this preview fell back to the house grey -- so a template
 * with no colour stored showed a grid everywhere except the format clients
 * open, and the toggle read as broken.
 */
export function findingCardGrid(config) {
  const cfg = config || {};
  const asked = typeof cfg.borderColor === 'string' ? cfg.borderColor.trim() : '';
  return {
    show: cfg.showBorders === true,
    color: /^#[0-9a-f]{3}([0-9a-f]{3})?$/i.test(asked) ? asked : FINDING_CARD_GRID_COLOR,
  };
}

const OWASP_CODES = OWASP_TOP10.map(o => o.code);

/** 'A04:2021 Insecure Design'. A space, as the renderers print it. */
function owaspRowLabel(code) {
  const hit = OWASP_TOP10.find(o => o.code === code);
  return hit ? `${hit.code} ${hit.name}` : '';
}

export function findingCardValue(finding, field) {
  const f = finding || {};
  const str = (v) => (v === undefined || v === null ? '' : String(v));
  switch (field) {
    case 'uid': return str(f.finding_uid);
    case 'title': return str(f.title);
    case 'severity': return str(f.severity);
    case 'cvssScore': {
      const score = f.cvss_score === undefined || f.cvss_score === null ? '' : String(f.cvss_score);
      const sev = str(f.severity);
      if (!score) return '';
      return sev ? `${score} (${sev})` : score;
    }
    case 'cvssScoreOnly':
      return f.cvss_score === undefined || f.cvss_score === null ? '' : String(f.cvss_score);
    case 'cvssVector': return str(f.cvss_tags);
    case 'status': return str(f.status);
    case 'cwe': return str(f.cwe);
    case 'owasp': {
      const code = str(f.owasp_category).trim();
      return OWASP_CODES.includes(code) ? owaspRowLabel(code) : '';
    }
    case 'exploitability': return str(f.direct_exploitability);
    case 'remediationEffort': return str(f.remediation_effort);
    case 'affectedAsset': return str(f.affected_endpoints);
    case 'details': return str(f.details);
    case 'observation': return str(f.observation);
    case 'stepsToReproduce': return str(f.steps_to_reproduce);
    case 'recommendation': return str(f.recommendation);
    case 'references': return str(f.references);
    default: return '';
  }
}

function cardRowList(given, fallback, extra) {
  const src = Array.isArray(given) ? given : fallback;
  return src
    .filter(r => r && typeof r === 'object' && FINDING_CARD_FIELD_KEYS.includes(r.field))
    .map(r => {
      const out = { label: typeof r.label === 'string' ? r.label : '', field: r.field };
      extra.forEach(k => { out[k] = r[k] === true; });
      return out;
    });
}

export function findingCardMetaRows(config) {
  return cardRowList((config || {}).metaRows, FINDING_CARD_META_ROWS, ['wide']);
}

export function findingCardBodySections(config) {
  return cardRowList((config || {}).bodySections, FINDING_CARD_BODY_SECTIONS, ['evidence']);
}

/** Two narrow rows share a line; a wide one takes a line of its own. */
export function findingCardLayout(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const out = [];
  for (let i = 0; i < list.length; i += 1) {
    const row = list[i];
    if (row.wide) { out.push({ left: row, right: null }); continue; }
    const next = list[i + 1];
    if (next && !next.wide) { out.push({ left: row, right: next }); i += 1; continue; }
    out.push({ left: row, right: null });
  }
  return out;
}

export function findingCardSeverityBg(severity, config) {
  const colours = { ...FINDING_CARD_SEVERITY_BG, ...((config || {}).severityColors || {}) };
  return colours[String(severity || '')] || FINDING_CARD_FALLBACK_BG;
}

export function findingCardFindings(findings, config) {
  const cfg = config || {};
  const rows = Array.isArray(findings) ? findings : [];
  const filter = Array.isArray(cfg.severityFilter) ? cfg.severityFilter : [];
  const kept = filter.length ? rows.filter(f => filter.includes(f && f.severity)) : rows;
  const limit = Number(cfg.maxCards) || 0;
  return limit > 0 ? kept.slice(0, limit) : kept;
}

/** Prose as plain paragraphs, the way both renderers flatten it. */
export function findingCardParagraphs(raw) {
  const text = String(raw === undefined || raw === null ? '' : raw)
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|h[1-6]|tr|blockquote)\s*>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/[ \t]+/g, ' ');
  return text.split(/\n+/).map(line => line.trim()).filter(Boolean);
}

/** A width from a template, clamped. See the backend copy for why. */
export function reportTwips(value, fallback, min = 200, max = 12000) {
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return fallback;
  return Math.min(max, Math.max(min, Math.round(n)));
}

export function findingCardConfig(config) {
  const cfg = config || {};
  return {
    metaRows: findingCardMetaRows(cfg),
    bodySections: findingCardBodySections(cfg),
    hideEmpty: cfg.hideEmpty !== false,
    emptyText: typeof cfg.emptyText === 'string' ? cfg.emptyText : FINDING_CARD_EMPTY,
    figurePrefix: typeof cfg.figurePrefix === 'string' ? cfg.figurePrefix : FINDING_CARD_FIGURE_PREFIX,
  };
}
